import { Spinner } from '@/components/glitch-ui/spinner';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Settings, ChevronDown, Loader2, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';
import type { OrganizationDataWithMembership } from '@/lib/types';

interface OrgSwitcherProps {
  isCollapsed?: boolean;
}

// #4623: searchbar appears once the account has more than 4 orgs — below that
// it adds friction without value (5 fit on screen with no scroll).
const SEARCH_THRESHOLD = 4;

export function OrgSwitcher({ isCollapsed = false }: OrgSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { organizations, organizationData, switchOrganization, isSwitching } = useOrganizationInitialization();
  const showSearch = (organizations?.length ?? 0) > SEARCH_THRESHOLD;
  const handleSwitch = async (org: OrganizationDataWithMembership) => {
    if (org.id === organizationData?.id) return;
    setIsOpen(false);
    await switchOrganization(org);
  };

  const filteredOrgs = useMemo(() => {
    const list = organizations ?? [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((o) => o.name?.toLowerCase().includes(q));
  }, [organizations, searchQuery]);

  // Reset query when the popover closes so the next open starts fresh.
  useEffect(() => {
    if (!isOpen) setSearchQuery('');
  }, [isOpen]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {isCollapsed ? (
          <button
            className="h-9 w-9 flex items-center justify-center text-gray-900 hover:bg-gray-50 rounded-lg transition-all cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#290BD9]"
            title={organizationData?.name || 'Organization'}
            disabled={isSwitching}
          >
            {isSwitching ? (
              <Spinner size="md" className="text-gray-400" />
            ) : organizationData?.primary_logo_url ? (
              <img src={organizationData.primary_logo_url} alt="" className="h-5 w-5 rounded object-cover" />
            ) : (
              <Building2 className="h-5 w-5" strokeWidth={1.5} />
            )}
          </button>
        ) : (
          <button className={`flex items-center gap-3 text-[13px] h-[38px] ${organizationData?.primary_logo_url ? 'pl-2' : 'pl-4'} pr-4 mx-1 w-[calc(100%-8px)] font-normal font-sans rounded-lg hover:bg-gray-50 transition-colors text-gray-900 cursor-pointer focus:outline-none`} disabled={isSwitching}>
            {isSwitching ? (
              <Loader2 className="!h-5 !w-5 shrink-0 animate-spin text-gray-400" />
            ) : organizationData?.primary_logo_url ? (
              <img src={organizationData.primary_logo_url} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
            ) : (
              <Building2 className="!h-5 !w-5 shrink-0" strokeWidth={1.5} />
            )}
            <span className="flex-1 text-left truncate">{isSwitching ? 'Switching...' : organizationData?.name || 'Select organization'}</span>
            {!isSwitching && <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        side={isCollapsed ? 'right' : 'bottom'}
        align="start"
        sideOffset={8}
        className="w-80 p-0 rounded-xl shadow-lg border border-gray-200"
        // Auto-focus the search input when present; otherwise let Radix pick the first row.
        onOpenAutoFocus={(e) => {
          if (showSearch) {
            e.preventDefault();
            searchInputRef.current?.focus();
          }
        }}
      >
        {showSearch && (
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search organizations..."
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>
        )}
        {/* #4623: cap at ~5 rows (~60px each) — overflowing rows scroll. */}
        <div className="py-2 max-h-[320px] overflow-y-auto">
          {filteredOrgs.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">
              No organizations match &ldquo;{searchQuery}&rdquo;
            </div>
          ) : filteredOrgs.map((org) => {
            const isSelected = org.id === organizationData?.id;
            return (
              <div
                key={org.id}
                role="button"
                tabIndex={0}
                onClick={() => handleSwitch(org)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSwitch(org); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer ${isSelected ? 'bg-[#290BD9]/5 border-l-2 border-l-[#290BD9]' : 'text-gray-700 hover:bg-gray-50 border-l-2 border-l-transparent'}`}
              >
                {org.primary_logo_url ? (
                  <img src={org.primary_logo_url} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
                ) : (
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${isSelected ? 'bg-[#290BD9]/10 text-[#290BD9]' : 'bg-gray-100 text-gray-600'}`}>
                    {org.name?.charAt(0)?.toUpperCase() || 'O'}
                  </div>
                )}
                <div className="flex-1 min-w-0 text-left">
                  <p className={`text-sm font-medium truncate ${isSelected ? 'text-[#290BD9]' : 'text-gray-900'}`}>{org.name}</p>
                  <p className="text-xs text-gray-500">
                    {org.user_count ?? 0} member{(org.user_count ?? 0) !== 1 ? 's' : ''} · <span className="font-medium text-[#290BD9]">{org.campaign_count ?? 0} campaign{(org.campaign_count ?? 0) !== 1 ? 's' : ''}</span>
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setIsOpen(false); navigate({ to: `/organizations/${org.id}` as string }); }}
                  className="shrink-0 p-1.5 text-gray-400 hover:text-[#290BD9] hover:bg-[#290BD9]/10 rounded-md transition-colors"
                  title="Organization Settings"
                >
                  <Settings className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
